import { useEffect, useMemo, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import type { MouseEvent, ReactElement } from 'react'
import type { FieldError } from 'react-hook-form'
import type { IBuilderConfig, IBuilderConfigSeries } from '~/types/graph-builder'

import BuilderFormSection from '../BuilderFormSection'
import GraphTypeButton from '../GraphTypeButton'

import DropdownMenu from '~/components/DropdownMenu'
import CheckboxInput from '~/components/Inputs/Basic/CheckboxInput'
import MultiSelectInput from '~/components/Inputs/ReactHookForm/MultiSelectInput'
import SelectInput from '~/components/Inputs/ReactHookForm/SelectInput'
import TextInput from '~/components/Inputs/ReactHookForm/TextInput'
import Wrapper from '~/components/Inputs/ReactHookForm/Wrapper'
import Label from '~/components/Label'
import { breakdownOptions, GRAPH_TYPE } from '~/const/graphs-builder'
import { useSearchParamsContext } from '~/providers/SearchParams'
import { useGetFieldsQuery } from '~/queries/graphs-builder'

const graphTypes = (Object.keys(GRAPH_TYPE) as (keyof typeof GRAPH_TYPE)[]).map((key) => GRAPH_TYPE[key])

export const defaultConfig: IBuilderConfigSeries = {
  defaultBreakdown: '',
  graphType: GRAPH_TYPE.LINE,
  minNbu: 100,
  normalizedBy: ['nbu'],
  values: ['iap_pred_360'],
  withInf: true,
  withSup: true,
  withTrend: true,
  yAxisName: '',
}

const SeriesFields = ({
  handleDeletion,
  handleGraphTypeOptionClick,
  handleSelectFieldChange,
  primaryColor,
  secondaryColor,
  seriesFieldsCount,
  seriesIndex,
  title,
  yAxisFieldsCount,
}: {
  handleDeletion: (seriesIndex: number) => void
  handleGraphTypeOptionClick: (graphType: GRAPH_TYPE) => void
  handleSelectFieldChange: (field: keyof IBuilderConfig, selectedOption: IOption | IOption[]) => void
  primaryColor?: string
  secondaryColor?: string
  seriesFieldsCount: number
  seriesIndex: number
  title: string
  yAxisFieldsCount: number
}): ReactElement => {
  /** Local state */

  const {
    control,
    formState: { errors },
    getValues,
    register,
    setValue,
    watch,
  } = useFormContext()

  const castedSeriesErrors = errors.series as unknown as Record<keyof IBuilderConfigSeries, FieldError>[]

  const { searchParamsObject } = useSearchParamsContext()

  const appKey = searchParamsObject.app?.split(':')[0]

  const featureName = watch('featureName')

  const { data: fields = [], isLoading: isFieldsLoading } = useGetFieldsQuery({ appKey, featureName })

  const [yAxisNameOptions, setYAxisNameOptions] = useState<IOption[]>([])

  const watchGraphType = watch(`series.${seriesIndex}.graphType`)

  const watchInfSupBounds = watch([`series.${seriesIndex}.withInf`, `series.${seriesIndex}.withSup`])

  const watchWantNegativeEvolution = watch('wantNegativeEvolution')

  const watchWithTrend = watch(`series.${seriesIndex}.withTrend`)

  const watchYAxes = watch('yAxes')

  const fieldsOptions = fields.map((value) => ({ label: value, value }))

  const isBoundsCheckboxChecked = watchInfSupBounds.every((value) => value)

  const menu = useMemo(
    () => [
      {
        items: [
          {
            label: 'Delete series',
            onClick: () => {
              handleDeletion(seriesIndex)
            },
          },
        ],
      },
    ],
    [handleDeletion, seriesIndex]
  )

  /** Handlers */

  const handleBoundsCheckboxChange = () => {
    setValue(`series.${seriesIndex}.withInf`, !watchInfSupBounds[0])
    setValue(`series.${seriesIndex}.withSup`, !watchInfSupBounds[1])
  }

  const handleGraphTypeClick = (type: GRAPH_TYPE) => () => {
    setValue(`series.${seriesIndex}.graphType`, type)
    handleGraphTypeOptionClick(type)
  }

  const handleTrendCheckboxChange = () => setValue(`series.${seriesIndex}.withTrend`, !watchWithTrend)

  const handleWantNegativeEvolutionChange = () => setValue('wantNegativeEvolution', !watchWantNegativeEvolution)

  /** Effects */

  useEffect(() => {
    setYAxisNameOptions(
      watchYAxes?.length ? watchYAxes.map(({ name }: { name: string }) => ({ label: name, value: name })) : []
    )
    if (watchYAxes?.length <= 1) setValue(`series.${seriesIndex}.yAxisName`, '1')
  }, [yAxisFieldsCount, watchYAxes, setValue, seriesIndex])

  return (
    <BuilderFormSection primaryColor={primaryColor} secondaryColor={secondaryColor} title={title}>
      {seriesFieldsCount > 1 ? (
        <DropdownMenu
          className="builder-form-section__dropdown-menu"
          menu={menu}
          onMouseDown={(e: MouseEvent) => e.stopPropagation()}
        />
      ) : null}
      <Wrapper className="graph-types-container" error={castedSeriesErrors?.[seriesIndex]?.graphType?.message}>
        <Label label="Graph type" required />
        <div className="graph-types">
          {graphTypes.map((type, index) => (
            <GraphTypeButton
              disabled={
                (type === GRAPH_TYPE.RAW || type === GRAPH_TYPE.STACKED_COLUMN_PERCENT) && seriesFieldsCount > 1
              }
              graphType={type}
              key={`${type}-${index}`}
              onClick={handleGraphTypeClick(type)}
              selectedGraphType={watchGraphType as GRAPH_TYPE}
              {...register(`series.${seriesIndex}.graphType`)}
            />
          ))}
        </div>
      </Wrapper>
      <Controller
        control={control}
        name={`series.${seriesIndex}.values`}
        render={() => (
          <MultiSelectInput
            error={castedSeriesErrors?.[seriesIndex]?.values?.message}
            isLoading={isFieldsLoading}
            label="Values"
            onChange={(value: unknown) =>
              handleSelectFieldChange(`series.${seriesIndex}.values` as keyof IBuilderConfig, value as IOption[])
            }
            options={fieldsOptions}
            placeholder="Select values"
            required
            value={fieldsOptions.filter((option) => getValues(`series.${seriesIndex}.values`)?.includes(option.value))}
          />
        )}
      />
      <Controller
        control={control}
        name={`series.${seriesIndex}.normalizedBy`}
        render={() => (
          <MultiSelectInput
            isLoading={isFieldsLoading}
            label="Normalized by"
            onChange={(value: unknown) =>
              handleSelectFieldChange(`series.${seriesIndex}.normalizedBy` as keyof IBuilderConfig, value as IOption[])
            }
            options={fieldsOptions}
            placeholder="Select values to normalize"
            value={fieldsOptions.filter(
              (option) => getValues(`series.${seriesIndex}.normalizedBy`)?.includes(option.value)
            )}
          />
        )}
      />
      {watchGraphType !== GRAPH_TYPE.RAW ? (
        <>
          <div className="checkbox-container">
            <CheckboxInput
              checked={watchWithTrend}
              label="Show trend"
              {...register(`series.${seriesIndex}.withTrend`)}
              onChange={handleTrendCheckboxChange}
            />
          </div>
          <div className="checkbox-container">
            <CheckboxInput
              checked={isBoundsCheckboxChecked}
              label="Show min/max bounds"
              {...register(`series.${seriesIndex}.withInf`)}
              {...register(`series.${seriesIndex}.withSup`)}
              onChange={handleBoundsCheckboxChange}
            />
          </div>
        </>
      ) : (
        <div className="checkbox-container">
          <CheckboxInput
            checked={watchWantNegativeEvolution}
            label="With negative evolution"
            onChange={handleWantNegativeEvolutionChange}
          />
        </div>
      )}
      {watchGraphType !== GRAPH_TYPE.RAW ? (
        <Controller
          control={control}
          name={`series.${seriesIndex}.defaultBreakdown`}
          render={({ field }) => (
            <SelectInput
              {...field}
              label="Default breakdown"
              onChange={(value: unknown) =>
                handleSelectFieldChange(
                  `series.${seriesIndex}.defaultBreakdown` as keyof IBuilderConfig,
                  value as IOption
                )
              }
              options={breakdownOptions}
              placeholder="Select a default breakdown"
              value={breakdownOptions.find(({ value }) => value === field.value)}
            />
          )}
        />
      ) : null}
      {watchGraphType !== GRAPH_TYPE.RAW ? (
        <TextInput
          error={castedSeriesErrors?.[seriesIndex]?.minNbu?.message}
          label="Min NBU"
          placeholder="Enter min NBU"
          required
          type="number"
          {...register(`series.${seriesIndex}.minNbu`, { valueAsNumber: true })}
        />
      ) : null}
      {watchGraphType !== GRAPH_TYPE.RAW ? (
        <Controller
          control={control}
          name={`series.${seriesIndex}.yAxisName`}
          render={({ field }) => (
            <SelectInput
              error={castedSeriesErrors?.[seriesIndex]?.yAxisName?.message}
              label="Y-Axis name"
              onChange={(value: unknown) =>
                handleSelectFieldChange(`series.${seriesIndex}.yAxisName` as keyof IBuilderConfig, value as IOption)
              }
              options={yAxisNameOptions.slice(0, yAxisFieldsCount)}
              placeholder="Y-axis name"
              required
              value={yAxisNameOptions.find(({ value }) => value === field.value)}
            />
          )}
        />
      ) : null}
    </BuilderFormSection>
  )
}

export default SeriesFields
